import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { AdditionalDetailsData, UIElement } from '@adyen/adyen-web';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { adyenService } from '@/services/adyenService';
import { ADYEN_3DS_CONTAINER_ID } from '@/constants/adyen';
import type { AdyenFinalizePaymentResponse } from '@/types/adyen';
import { useGetStatus } from '@/api/status';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { logError, logInfo } from '@/utils/logger';
import { getAdyenConfig } from '@/utils/adyen';

export const AdyenCard3DSForm = () => {
  const [adyenComponent, setAdyenComponent] = useState<UIElement | null>(null);
  const {
    customer,
    paymentMethodsSettings,
    paymentMethodSelected,
    threeDSSecurityState: { params, provider, handlePaymentSuccess, handlePaymentError },
    actions: { setThreeDSSecurityState },
    card: { adyenCardDetailsRef },
  } = useContext(CheckoutContext);
  const getStatus = useGetStatus();
  const isInitializedRef = useRef(false);
  const componentRef = useRef<UIElement | null>(null);

  const paymentMethodId = useMemo(() => {
    return (
      paymentMethodsSettings.find(
        (method) => method.paymentMethodName === PaymentMethodEnum.CREDIT_CARD && method.provider === ProviderEnum.ADYEN
      )?.id ?? ''
    );
  }, [paymentMethodsSettings]);

  useEffect(() => {
    if (adyenComponent) {
      adyenComponent.mount(document.getElementById(ADYEN_3DS_CONTAINER_ID) as HTMLElement);
    }
  }, [adyenComponent]);

  useEffect(() => {
    if (!params || provider !== ProviderEnum.ADYEN) {
      isInitializedRef.current = false;
      return;
    }

    if (isInitializedRef.current) return;

    isInitializedRef.current = true;

    if (handlePaymentSuccess) {
      adyenService.updateOnPaymentSuccess(handlePaymentSuccess);
    }
    if (handlePaymentError) {
      adyenService.updateOnPaymentError(handlePaymentError);
    }

    const createComponent = async () => {
      const adyenConfig = getAdyenConfig(
        paymentMethodsSettings as unknown[],
        paymentMethodSelected ?? PaymentMethodEnum.CREDIT_CARD
      );
      if (adyenConfig) {
        await adyenService.initialize(adyenConfig, customer);
      }

      const AdyenComponent = await adyenService.createFromAction(
        (params as AdyenFinalizePaymentResponse).adyen.action!,
        {
          onAdditionalDetails: async (state: AdditionalDetailsData) => {
            const { resultCode } = await getStatus.mutateAsync({
              paymentMethodId,
              sessionToken: state.data.details.threeDSResult ?? '',
            });

            if (adyenCardDetailsRef.current.actions) {
              if (resultCode) {
                adyenCardDetailsRef.current.actions.resolve({ resultCode });
              } else {
                adyenCardDetailsRef.current.actions.reject();
              }
            } else {
              if (resultCode === 'Authorised') {
                logInfo(InfoLogs.adyen3dsPaymentSuccess, {
                  paymentMethodId,
                });
                adyenService.callOnPaymentSuccess({ resultCode });
              } else {
                logError(ErrorLogs.adyen3dsPaymentError, {
                  paymentMethodId,
                });
                adyenService.callOnPaymentError();
              }
            }
            setThreeDSSecurityState({ show3DSForm: false });
          },
        }
      );
      componentRef.current = AdyenComponent as UIElement;
      setAdyenComponent(AdyenComponent as UIElement);
    };

    createComponent();

    return () => {
      isInitializedRef.current = false;
      if (componentRef.current) {
        componentRef.current.unmount();
        componentRef.current = null;
      }
      setAdyenComponent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, provider]);

  if (!params || provider !== ProviderEnum.ADYEN) return null;

  return (
    <div className="fixed inset-0 z-150 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-0 md:mx-4 pt-2 pb-2 flex flex-col max-h-[50rem]">
        <h2 className="text-xl font-semibold mb-4 w-full px-5">Secure Payment Verification</h2>
        <div id={ADYEN_3DS_CONTAINER_ID} className="w-full min-h-[25rem] px-5 overflow-auto" />
      </div>
    </div>
  );
};
